import React, { useState, useEffect } from "react";
import {
  Globe,
  Monitor,
  Smartphone,
  Eye,
  Code,
  Database,
  Cloud,
  Bot,
  Layers,
  Folder,
  Search,
  X,
  Loader,
} from "lucide-react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { dataportfolio, meta } from "content_option";
import PageHeader from "components/Header/PageHeader";
import { Helmet, HelmetProvider } from "react-helmet-async";

// Mock data remains the same as provided

// Image Carousel Modal Component
const ImageCarouselModal = ({ images, title, onClose }) => {
  if (!images || images.length === 0) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={24} />
        </button>
        <h2 className="modal-title">{title} Screenshots</h2>
        <Carousel
          showArrows={true}
          infiniteLoop={true}
          dynamicHeight={false}
          className="image-carousel"
        >
          {images.map((image, index) => (
            <div key={index}>
              <img src={image} alt={`${title} screenshot ${index + 1}`} />
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ videoUrl, title, onClose }) => {
  if (!videoUrl) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="flex justify-content-center align-items-center modal-content video-modal-content"
        style={{ alignItems: "center", justifyContent: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-btn" onClick={onClose}>
          <X size={24} />
        </button>
        <h2 className="modal-title">{title} Demo</h2>
        <div
          className="flex justify-content-center align-items-center"
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          <video
            src={videoUrl}
            controls
            autoPlay
            loop
            muted
            style={{ height: 500 }}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  );
};

export function Portfolio() {
  const [activeTab, setActiveTab] = useState("websites");
  const [searchTerm, setSearchTerm] = useState("");
  const [showImageCarousel, setShowImageCarousel] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const tabs = [
    { id: "websites", label: "Websites", icon: Globe },
    { id: "desktop", label: "Desktop Apps", icon: Monitor },
    { id: "mobile", label: "Mobile Apps", icon: Smartphone },
  ];

  const getProjectIcon = (project) => {
    if (!project || !project.techStack) return Folder;

    const techStack = project.techStack.join(" ").toLowerCase();

    if (
      techStack.includes("react") ||
      techStack.includes("vue") ||
      techStack.includes("angular")
    ) {
      return Code;
    }
    if (
      techStack.includes("node") ||
      techStack.includes("express") ||
      techStack.includes("backend")
    ) {
      return Database;
    }
    if (
      techStack.includes("web") ||
      techStack.includes("html") ||
      techStack.includes("css")
    ) {
      return Globe;
    }
    if (
      techStack.includes("mobile") ||
      techStack.includes("react native") ||
      techStack.includes("flutter")
    ) {
      return Smartphone;
    }
    if (techStack.includes("electron") || techStack.includes("desktop")) {
      return Monitor;
    }
    if (
      techStack.includes("cloud") ||
      techStack.includes("aws") ||
      techStack.includes("azure")
    ) {
      return Cloud;
    }
    if (
      techStack.includes("ai") ||
      techStack.includes("ml") ||
      techStack.includes("machine learning")
    ) {
      return Bot;
    }
    return Layers;
  };

  // Filter projects based on active tab and search
  const filteredProjects = dataportfolio.filter((project) => {
    const matchesTab = project.category === activeTab;
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const handleViewDemoClick = (project) => {
    setSelectedProject(project);
    if (project.category === "desktop" && project.images) {
      setShowImageCarousel(true);
    } else if (project.category === "mobile" && project.video) {
      setShowVideoModal(true);
    } else if (
      (project.previewLink && project.category === "websites") ||
      "desktop"
    ) {
      window.open(project.previewLink, "_blank");
    }
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <Loader className="spinner" size={48} />
        <style jsx>{`
          .loader-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            background: #0d1117;
          }
          .spinner {
            animation: spin 1s linear infinite;
            color: #ff0080;
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Portfolio | {meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>
      <div className="portfolio-container">
        <PageHeader
          title="My Portfolio"
          breadcrumb={[
            { label: "Home", path: "/" },
            { label: "Portfolio", path: "/portfolio" },
          ]}
          backgroundImage="https://colorlib.com/wp/wp-content/uploads/sites/2/27_website_mockup.jpg"
        />
        <style jsx>{`
          :root {
            --bg-color: #0d1117;
            --text-color: #ffffff;
            --text-color-2: #b0c2d2;
            --secondary-color: #ff0080;
          }

          .portfolio-container {
            min-height: 100vh;
            background: var(--bg-color);
            color: var(--text-color);
            font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
            position: relative;
            overflow-x: hidden;
          }

          .portfolio-content {
            position: relative;
            z-index: 1;
            padding: 2rem;
            max-width: 1400px;
            margin: 0 auto;
          }

          .hero-section {
            text-align: center;
            margin-bottom: 3rem;
          }

          .hero-title {
            background: linear-gradient(
              135deg,
              var(--text-color) 0%,
              var(--secondary-color) 100%
            );
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            font-size: 3.5rem;
            font-weight: 800;
            margin-bottom: 1rem;
            letter-spacing: -0.02em;
            line-height: 1.1;
          }

          .hero-subtitle {
            color: var(--text-color-2);
            font-size: 1.2rem;
            font-weight: 400;
            max-width: 600px;
            margin: 0 auto;
            opacity: 0.9;
            line-height: 1.6;
          }

          .search-section {
            margin-bottom: 2rem;
            display: flex;
            justify-content: center;
          }

          .search-container {
            position: relative;
            max-width: 400px;
            width: 100%;
          }

          .search-input {
            width: 100%;
            padding: 12px 20px 12px 50px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 50px;
            color: var(--text-color);
            font-size: 1rem;
            transition: all 0.3s ease;
          }

          .search-input:focus {
            outline: none;
            border-color: var(--secondary-color);
            box-shadow: 0 0 10px rgba(255, 0, 128, 0.2);
          }

          .search-input::placeholder {
            color: var(--text-color-2);
            opacity: 0.7;
          }

          .search-icon {
            position: absolute;
            left: 18px;
            top: 50%;
            transform: translateY(-50%);
            color: var(--text-color-2);
            opacity: 0.7;
          }

          .tabs-container {
            display: flex;
            justify-content: center;
            margin-bottom: 3rem;
            background: rgba(255, 255, 255, 0.02);
            border-radius: 20px;
            padding: 8px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 3rem;
          }

          .tab {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 14px 20px;
            border-radius: 14px;
            background: transparent;
            border: none;
            color: var(--text-color-2);
            font-size: 1rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .tab.active {
            color: white;
            background: linear-gradient(
              135deg,
              var(--secondary-color),
              #ff69b4
            );
            box-shadow: 0 5px 15px rgba(255, 0, 128, 0.25);
          }

          .tab:hover:not(.active) {
            color: var(--text-color);
            background: rgba(255, 255, 255, 0.05);
          }

          .projects-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
            gap: 2rem;
            margin-top: 2rem;
          }

          .project-card {
            background: rgba(255, 255, 255, 0.03);
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            overflow: hidden;
            transition: all 0.3s ease;
            position: relative;
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          .project-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3),
              0 0 0 1px rgba(255, 255, 255, 0.1);
          }

          .card-image-container {
            position: relative;
            height: 220px;
            overflow: hidden;
          }

          .card-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.4s ease;
          }

          .project-card:hover .card-image {
            transform: scale(1.05);
          }

          .card-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(
              135deg,
              rgba(0, 0, 0, 0.1) 0%,
              rgba(0, 0, 0, 0.4) 100%
            );
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .project-card:hover .card-overlay {
            opacity: 1;
          }

          .card-icon {
            position: absolute;
            top: 16px;
            right: 16px;
            background: rgba(255, 255, 255, 0.15);
            border-radius: 50%;
            padding: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            transition: all 0.3s ease;
            z-index: 2;
          }

          .project-card:hover .card-icon {
            background: rgba(255, 255, 255, 0.25);
          }

          .status-badge {
            position: absolute;
            top: 16px;
            left: 16px;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            z-index: 2;
          }

          .status-completed {
            background: rgba(34, 197, 94, 0.2);
            color: #22c55e;
            border: 1px solid rgba(34, 197, 94, 0.3);
          }

          .status-progress {
            background: rgba(251, 191, 36, 0.2);
            color: #fbbf24;
            border: 1px solid rgba(251, 191, 36, 0.3);
          }

          .card-content {
            padding: 1.5rem;
            position: relative;
            z-index: 2;
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .card-title {
            color: var(--text-color);
            font-size: 1.25rem;
            font-weight: 700;
            margin-bottom: 0.75rem;
            line-height: 1.3;
          }

          .card-description {
            color: var(--text-color-2);
            font-size: 0.9rem;
            line-height: 1.6;
            margin-bottom: 1.25rem;
            opacity: 0.9;
            flex: 1;
          }

          .tech-stack {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 1.5rem;
          }

          .tech-tag {
            background: rgba(255, 255, 255, 0.08);
            color: var(--text-color-2);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 4px 10px;
            font-size: 0.75rem;
            font-weight: 500;
            transition: all 0.3s ease;
          }

          .view-demo-btn {
            background: linear-gradient(
              135deg,
              var(--secondary-color),
              #ff69b4
            );
            color: white;
            border: none;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            cursor: pointer;
            position: relative;
            overflow: hidden;
          }

          .view-demo-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(255, 0, 128, 0.3);
          }

          .no-results {
            text-align: center;
            padding: 4rem 2rem;
            background: rgba(255, 255, 255, 0.02);
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            margin-top: 2rem;
          }

          .no-results h3 {
            color: var(--text-color);
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }

          .no-results p {
            color: var(--text-color-2);
            opacity: 0.8;
          }

          /* Modal Styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .modal-content {
            background: var(--bg-color);
            border-radius: 20px;
            padding: 2rem;
            position: relative;
            max-width: 90vw;
            max-height: 90vh;
            overflow: auto;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .modal-title {
            color: var(--text-color);
            font-size: 1.8rem;
            margin-bottom: 1.5rem;
            text-align: center;
          }

          .modal-close-btn {
            position: absolute;
            top: 15px;
            right: 15px;
            background: transparent;
            border: none;
            color: var(--text-color-2);
            cursor: pointer;
            font-size: 1.5rem;
            padding: 10px;
            border-radius: 50%;
            transition: background 0.2s ease, color 0.2s ease;
          }

          .modal-close-btn:hover {
            background: rgba(255, 255, 255, 0.1);
            color: var(--secondary-color);
          }

          /* Carousel Styles */
          .carousel .slide img {
            max-height: 70vh;
            width: auto;
            margin: auto;
            border-radius: 10px;
          }

          /* Video Modal Specific Styles */
          .video-modal-content {
            max-width: 800px;
            width: 100%;
          }

          @media (max-width: 768px) {
            .portfolio-content {
              padding: 1rem;
            }

            .hero-title {
              font-size: 2.5rem;
            }

            .tabs-container {
              flex-direction: column;
              gap: 8px;
              max-width: 300px;
            }

            .projects-grid {
              grid-template-columns: 1fr;
              gap: 1.5rem;
            }

            .project-card:hover {
              transform: none;
            }

            .modal-content {
              padding: 1.5rem;
            }
            .modal-title {
              font-size: 1.5rem;
            }
          }
        `}</style>

        <div className="portfolio-content">
          {/* Hero Section */}
          <div className="hero-section">
            {/* <h1 className="hero-title">My Portfolio</h1>
          <p className="hero-subtitle">
            Crafting digital experiences with cutting-edge technologies and
            innovative solutions across web, desktop, and mobile platforms.
          </p> */}
          </div>

          {/* Search Section */}
          <div className="search-section">
            <div className="search-container">
              <Search className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Search projects..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs-container">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Projects Grid */}
          {filteredProjects.length > 0 ? (
            <div className="projects-grid">
              {filteredProjects.map((project, index) => {
                const IconComponent = getProjectIcon(project);
                return (
                  <div key={index} className="project-card">
                    <div className="card-image-container">
                      <img
                        alt={project.title}
                        src={project.img}
                        className="card-image"
                      />
                      <div className="card-overlay" />

                      <div className="card-icon">
                        <IconComponent size={20} />
                      </div>

                      <div
                        className={`status-badge ${
                          project.status === "completed"
                            ? "status-completed"
                            : "status-progress"
                        }`}
                      >
                        {project.status === "completed"
                          ? "Completed"
                          : "In Progress"}
                      </div>
                    </div>

                    <div className="card-content">
                      <h3 className="card-title">{project.title}</h3>
                      <p className="card-description">{project.description}</p>

                      {/* Tech Stack */}
                      <div className="tech-stack">
                        {project.techStack.map((tech, i) => (
                          <span key={i} className="tech-tag">
                            {tech}
                          </span>
                        ))}
                      </div>

                      {/* View Demo Button */}
                      <button
                        className="view-demo-btn"
                        onClick={() => handleViewDemoClick(project)}
                      >
                        <Eye size={16} />
                        View Demo
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-results">
              <h3>No projects found</h3>
              <p>
                Try adjusting your search criteria to discover more projects
              </p>
            </div>
          )}
        </div>

        {/* Render Modals conditionally */}
        {showImageCarousel && selectedProject && (
          <ImageCarouselModal
            images={selectedProject.images}
            title={selectedProject.title}
            onClose={() => setShowImageCarousel(false)}
          />
        )}

        {showVideoModal && selectedProject && (
          <VideoModal
            videoUrl={selectedProject.video}
            title={selectedProject.title}
            onClose={() => setShowVideoModal(false)}
          />
        )}
      </div>
    </HelmetProvider>
  );
}
